import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import KPICard from "../components/KPICard";
import RAGBadge from "../components/RAGBadge";
import { useTheme } from "../DataContext";
import { DEMO_PROJECTS, generateMonthlyBurnData } from "../demoData";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, LineChart, Line, Legend, Cell,
} from "recharts";

const fmt = (n) => {
  if (n >= 1000000)  return `$${(n/1000000).toFixed(2)}M`;
  if (n >= 1000)     return `$${(n/1000).toFixed(0)}K`;
  return `$${n}`;
};

export default function FinancialPerformance() {
  const { theme } = useTheme();
  const [selected, setSelected] = useState(DEMO_PROJECTS[0]);

  const totalBudget = DEMO_PROJECTS.reduce((s, p) => s + p.budget, 0);
  const totalSpent  = DEMO_PROJECTS.reduce((s, p) => s + p.spent, 0);
  const totalEAC    = DEMO_PROJECTS.reduce((s, p) => {
    const ev = p.budget * (p.pct / 100);
    return s + p.spent + (p.budget - ev) / p.cpi;
  }, 0);
  const totalVAC    = totalBudget - totalEAC;
  const burnPct     = Math.round(totalSpent / totalBudget * 100);

  const burnData = generateMonthlyBurnData(selected);

  // Monthly portfolio-level cash flow
  const monthlyFlow = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => ({
    month: m,
    planned:    Math.round(totalBudget / 12),
    actual:     Math.round(totalSpent / 5 * (i < 5 ? 1 + (i * 0.04) : 0)),
    forecast:   i >= 5 ? Math.round(totalBudget / 12 * 1.06) : 0,
    cumActual:  Math.round(totalSpent * (i + 1) / 5),
    cumPlanned: Math.round(totalBudget * (i + 1) / 12),
  }));

  return (
    <PageLayout
      title="Financial Performance"
      subtitle="Budget burn rate, cost variance, cash flow forecast, ROI trajectory — P-06"
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        <KPICard label="Portfolio Budget"  value={fmt(totalBudget)} icon="▦" color={theme.accent}  sub="Total approved" />
        <KPICard label="Total Spent"       value={fmt(totalSpent)}  icon="◉" color={theme.cyan}    sub={`${burnPct}% consumed`} trend={burnPct - 50} />
        <KPICard label="Forecast at Completion" value={fmt(totalEAC)} icon="◈" color={totalEAC > totalBudget ? theme.danger : theme.success}
          sub={`EAC vs BAC: ${fmt(Math.abs(totalVAC))} ${totalVAC >= 0 ? "under" : "over"}`} />
        <KPICard label="Variance at Completion" value={fmt(Math.abs(totalVAC))} icon="⚠"
          color={totalVAC >= 0 ? theme.success : theme.danger}
          sub={totalVAC >= 0 ? "Under budget" : "OVER BUDGET"} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "24px" }}>
        <KPICard label="Over-Budget Projects" value={DEMO_PROJECTS.filter(p => p.cpi < 0.95).length} color={theme.danger}  sub="CPI < 0.95" />
        <KPICard label="On-Budget Projects"   value={DEMO_PROJECTS.filter(p => p.cpi >= 1).length}    color={theme.success} sub="CPI ≥ 1.00" />
        <KPICard label="Avg Burn Rate"        value={fmt(totalSpent / 5)}                              color={theme.warning} sub="Per month (YTD)" />
        <KPICard label="Projected Overrun"    value={fmt(Math.max(0, totalEAC - totalBudget))}         color={theme.danger}  sub="Above BAC" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
        {/* Portfolio Cash Flow */}
        <div className="pmo-card">
          <div className="section-title">Portfolio Monthly Cash Flow 2025</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={monthlyFlow} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
              <defs>
                <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.accent} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={theme.accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis dataKey="month" tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <YAxis tickFormatter={v => fmt(v)} tick={{ fill: theme.textMuted, fontSize: 9 }} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
              <Area type="monotone" dataKey="planned"  stroke={`${theme.textMuted}88`} fill="none" strokeDasharray="4 4" strokeWidth={1.5} name="Planned" />
              <Area type="monotone" dataKey="actual"   stroke={theme.accent}  fill="url(#actGrad)" strokeWidth={2} name="Actual" />
              <Area type="monotone" dataKey="forecast" stroke={theme.warning} fill="none" strokeDasharray="2 3" strokeWidth={1.5} name="Forecast" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Project Budget vs Spent */}
        <div className="pmo-card">
          <div className="section-title">Project Budget vs Actual Spend</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={DEMO_PROJECTS.map(p => ({ name: p.id, budget: p.budget, spent: p.spent, rag: p.rag }))}
              margin={{ top: 5, right: 10, bottom: 0, left: -10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis dataKey="name" tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <YAxis tickFormatter={v => fmt(v)} tick={{ fill: theme.textMuted, fontSize: 9 }} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
              <Bar dataKey="budget" name="Budget" fill={`${theme.textMuted}44`} radius={[4,4,0,0]} />
              <Bar dataKey="spent"  name="Spent"  radius={[4,4,0,0]}>
                {DEMO_PROJECTS.map((p, i) => <Cell key={i} fill={p.cpi < 0.85 ? theme.danger : p.cpi < 1 ? theme.warning : theme.success} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Selected Project Burn Detail */}
      <div className="pmo-card" style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <div className="section-title" style={{ marginBottom: 0 }}>Project Burn Detail</div>
          <select value={selected.id} onChange={e => setSelected(DEMO_PROJECTS.find(p => p.id === e.target.value))}
            style={{ padding: "5px 10px", fontSize: "12px" }}>
            {DEMO_PROJECTS.map(p => <option key={p.id} value={p.id}>{p.id} — {p.name}</option>)}
          </select>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={burnData} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
            <XAxis dataKey="month" tick={{ fill: theme.textMuted, fontSize: 10 }} />
            <YAxis tickFormatter={v => fmt(v)} tick={{ fill: theme.textMuted, fontSize: 9 }} />
            <Tooltip formatter={v => fmt(v)} contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
            <Legend wrapperStyle={{ fontSize: "11px" }} />
            <Line type="monotone" dataKey="planned"  stroke={theme.cyan}    strokeWidth={2} dot={false} name="Planned" />
            <Line type="monotone" dataKey="actual"   stroke={theme.accent}  strokeWidth={2} dot={false} name="Actual" />
            <Line type="monotone" dataKey="forecast" stroke={theme.warning} strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Forecast (EAC)" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Financial Summary Table */}
      <div className="pmo-card">
        <div className="section-title">Financial Summary — All Projects</div>
        <table>
          <thead>
            <tr>{["ID","Project","Budget (BAC)","Spent (AC)","EV","EAC","VAC","CPI","Burn %","Over-Budget Risk","RAG"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {DEMO_PROJECTS.sort((a, b) => a.cpi - b.cpi).map(p => {
              const ev  = p.budget * (p.pct / 100);
              const eac = p.spent + (p.budget - ev) / p.cpi;
              const vac = p.budget - eac;
              const burnP = Math.round(p.spent / p.budget * 100);
              return (
                <tr key={p.id}>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.accent, fontSize: "12px" }}>{p.id}</td>
                  <td style={{ color: theme.textPrimary, fontWeight: 500 }}>{p.name}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace" }}>{fmt(p.budget)}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace" }}>{fmt(p.spent)}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.cyan }}>{fmt(ev)}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: eac > p.budget ? theme.danger : theme.success }}>{fmt(eac)}</td>
                  <td style={{ fontFamily: "'Share Tech Mono',monospace", color: vac >= 0 ? theme.success : theme.danger }}>{fmt(Math.abs(vac))}</td>
                  <td style={{ color: p.cpi >= 1 ? theme.success : p.cpi >= 0.9 ? theme.warning : theme.danger, fontFamily: "'Share Tech Mono',monospace", fontWeight: 700 }}>{p.cpi.toFixed(3)}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <div style={{ width: "50px", height: "4px", background: theme.cardBorder, borderRadius: "2px" }}>
                        <div style={{ width: `${Math.min(burnP, 100)}%`, height: "100%", background: burnP > 90 ? theme.danger : burnP > 70 ? theme.warning : theme.success, borderRadius: "2px" }} />
                      </div>
                      <span style={{ fontSize: "11px" }}>{burnP}%</span>
                    </div>
                  </td>
                  <td>
                    <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: 700, background: p.cpi < 0.9 ? `${theme.danger}20` : p.cpi < 1 ? `${theme.warning}20` : `${theme.success}20`, color: p.cpi < 0.9 ? theme.danger : p.cpi < 1 ? theme.warning : theme.success }}>
                      {p.cpi < 0.9 ? "HIGH" : p.cpi < 1 ? "MEDIUM" : "LOW"}
                    </span>
                  </td>
                  <td><RAGBadge status={p.rag} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
